import React, { useRef, useState } from "react";
import { Upload, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import '../../styles/admin/StorageVideoUpload.css';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:5000");

function authHeaders() {
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Upload a private program video to B2 and return storage file_uuid.
 * HLS is queued asynchronously (no processing UI).
 * pathSegments mirror (object key under bucket — no media/ prefix):
 *   Programs/{Program}/Workouts/Phase {n}/Day {n}
 *   Programs/{Program}/Warmup
 *   Programs/{Program}/Intro
 */
export default function StorageVideoUpload({
  value = "",
  onChange,
  pathSegments = [],
  isPrivate = true,
  disabled = false,
  label = "Upload",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [lastPath, setLastPath] = useState("");

  const handleFile = async (file) => {
    if (!file || disabled) return;

    setBusy(true);
    setError("");
    setStatus("Creating upload target…");

    try {
      const targetsRes = await fetch(`${API_URL}/api/storage/upload-targets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          fileName: file.name,
          type: "video",
          size: file.size,
          isPrivate: isPrivate ? 1 : 0,
          pathSegments,
        }),
      });

      const targetsData = await targetsRes.json();
      if (!targetsRes.ok) {
        throw new Error(targetsData.error || targetsData.message || "upload-targets failed");
      }

      const { fileUuid, filePath, targets, hlsEnabled } = targetsData;
      const target = targets?.[0];
      if (!target?.uploadUrl) throw new Error("No B2 upload URL returned");

      setStatus("Uploading to B2…");
      setLastPath(filePath);

      const uploadRes = await fetch(target.uploadUrl, {
        method: "POST",
        headers: {
          Authorization: target.authorizationToken,
          "X-Bz-File-Name": encodeURIComponent(filePath),
          "Content-Type": file.type || "b2/x-auto",
          "X-Bz-Content-Sha1": "do_not_verify",
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error(`B2 upload failed (${uploadRes.status})`);
      }

      // Fire-and-forget HLS — do not block admin UI
      if (hlsEnabled) {
        fetch(`${API_URL}/api/storage/${fileUuid}/process`, {
          method: "POST",
          headers: authHeaders(),
        }).catch((err) => {
          console.warn("HLS queue request failed", err);
        });
      }

      onChange?.(fileUuid);
      setStatus("Uploaded");
    } catch (err) {
      console.error(err);
      setError(err.message || "Upload failed");
      setStatus("");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="admin-storage-video-upload">
      <div className="admin-storage-video-upload-buttons">
        <button
          type="button" className="upload"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}>
          {busy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
          {busy ? "Uploading…" : 'Upload'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          hidden
          disabled={disabled || busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {value ? (
          <button
            type="button"
            className="clear"
            disabled={busy}
            onClick={() => {
              onChange?.("");
              setStatus("");
              setLastPath("");
              setError("");
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            title="Clear linked storage video"
          >
            <Trash2 size={14} />
            Clear
          </button>
        ) : null}
      </div>

      {value ? (
        <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <CheckCircle2 size={14} color="#0f766e" />
          <span>
            Linked B2 video <code style={{ fontSize: 12 }}>{value}</code>
          </span>
        </div>
      ) : null}

      {(status || lastPath) && (
        <div style={{ fontSize: 12, opacity: 0.8, wordBreak: "break-all" }}>
          {status}
          {lastPath ? (
            <>
              <br />
              Path: <code>{lastPath}</code>
            </>
          ) : null}
        </div>
      )}

      {error ? (
        <div style={{ fontSize: 12, color: "#b91c1c" }}>{error}</div>
      ) : null}

      {pathSegments?.length ? (
        <div style={{ fontSize: 11, opacity: 0.65, wordBreak: "break-all" }}>
          Folder: {pathSegments.join(" / ")} / {"{uuid}"}
        </div>
      ) : null}

      
        {/* <small style={{ opacity: 0.75, marginTop: 10 }}> 
          Youtube link acts as a fallback when B2 Video is not available.
          After upload, click <strong>Save</strong>. Processing will take a few minutes. till then, the video will not be available and youtube link acts as a fallback.
        </small> */}
    </div>
  );
}
